<template>
    <div class="container" 
        @dragleave.capture="handleDragLeave" 
        @dragenter="handleContainerDragEnterOrOver" 
        @dragover.self="handleContainerDragEnterOrOver" 
        @drop.self="handleContainerDragDrop"
        @mousedown.self="selectedRow = null">

        <div v-if="error && error.row == null" class="error">{{ error.message }}</div>
        <template v-for="row in rows">
            <expression-tree-row 
                :row="row" 
                :indentWidth="indentWidth"
                :selected="selectedRow == row"
                @click="selectedRow = row"
                @row-dragging-started="handleRowDraggingStarted" 
                @dragOver="handleRowDragOver" 
                @dragDrop="handleRowDragDrop" 
                @dragLeave="handleDragLeave" />
            <div v-if="error && error.row == row" class="error">{{ error.message }}</div>
        </template>
        <expression-tree-dropper :position="dropperPosition" />

        <portal to="destination">
            <div class="buttons">
                <ui-button icon="delete" :disabled="selectedRow == null" color="primary" size="small" @click="handleDelete">Delete Item</ui-button>
                <ui-button icon="content_cut" :disabled="selectedRow == null || selectedRow.kind == 'Fragment'" color="primary" size="small" @click="handleMoveToFragment">Move to Fragment</ui-button>
                <ui-button icon="article" color="primary" size="small" @click="handleShowXml"></ui-button>
            </div>
        </portal>

        <ui-modal ref="toFragmentModal" title="Move to Fragment" @reveal="$refs.toFragmentName.focus()">
            <ui-textbox label="Fragment Name" ref="toFragmentName" v-model="toFragmentName"></ui-textbox>
            <div slot="footer">
                <ui-button :disabled="toFragmentName.length == 0" @click="handleCompleteMoveToFragment" color="primary">Move</ui-button>
            </div>
        </ui-modal>

        <ui-modal ref="showXmlModal" title="Exported Node">
            <pre v-if="geofence != null">{{ showXmlContents }}</pre>
        </ui-modal>
    </div>
</template>

<script>
import NodeEditor from "./NodeEditor";
import ExpressionTreeRow from "./ExpressionTreeRow";
import ExpressionTreeDropper from "./ExpressionTreeDropper";

import * as nodes from "@/nodes";
import { unpackNodeDragSource } from "@/utilities.js";
import geofences from "@/geofences.js";

function flattenTree(root)
{
    let into = [];

    flattenTreeRecurse(root, into, -1, null);

    return into;
}

function flattenTreeRecurse(node, into, indent, parentNode)
{
    let row = { 
        key: into.length, 
        kind: node.name,
        indent: indent, 
        name: node.description, 
        node,
        parentNode,
        drops: [],
        keysRange: [into.length, null],
        properties: node.properties
    };

    if (parentNode != null) into.push(row);

    if (node instanceof nodes.ContainerExpression) {
        for (let child of node.children)
        {
            flattenTreeRecurse(child, into, indent + 1, node);

            if (node.couldAdd()) {
                let lastRow = into[into.length - 1];

                lastRow.drops.splice(0, 0, {
                    indent: indent + 1,
                    container: node,
                    after: child,
                    containerKey: row.key
                });
            }
        }

        if (node.couldAdd()) {
            row.drops.splice(0, 0, {
                indent: indent + 1,
                container: node,
                after: null,
                containerKey: row.key
            });
        }
    }

    row.keysRange[1] = into.length;
}

export default {
    name: 'expression-tree',
    components: { NodeEditor, ExpressionTreeRow, ExpressionTreeDropper },
    data() {
        return {
            indentWidth: 20,
            rows: flattenTree(this.geofence.root),
            addingAtOffset: null,
            addingToRow: null,
            dropperPosition: null,
            selectedRow: null,
            toFragmentName: "",
            showXmlContents: ""
        }
    },
    props: {
        geofence: { type: Object, required: true }
    },
    computed: {
        allNodeProperties() {
            return this.rows.map(row => row.properties);
        },
        error() {
            if (window.bus.result.type != nodes.Result.errorType) return null;

            return {
                row: this.rows.find(row => window.bus.result.value.node.id == row.node.id),
                message: window.bus.result.value.message
            };
        }
    },
    watch: {
        geofence() {
            this.updateTree(false);
        },
        allNodeProperties: {
            deep: true,
            handler() {
                window.bus.notifyGeofenceNodeChanged(this.geofence);
            }
        }
    },
    methods: {
        handleClickAdd(row, e) {
            let bounds = e.target.getBoundingClientRect();

            this.addingToRow = row;
            this.addingAtOffset = { x: bounds.left, y: bounds.bottom + 5 };
        },
        handleCloseAdd() {
            this.addingToRow = null;
            this.addingAtOffset = null;
        },
        createTopLevel(constructor) {

        },
        handleCreate(constructor) {
            this.addingToRow.parent.add(new constructor());

            this.addingToRow = null;
            this.addingAtOffset = null;

            this.updateTree(true);
        },
        _findDrop(e) {
            // Find the set of drops appropriate for the position:
            let drops;

            let dropsRowIndex = e.row.key - 1 + e.rowOffset;

            if (0 <= dropsRowIndex) {
                drops = this.rows[dropsRowIndex].drops;
            } else {
                drops = [{
                    indent: 0,
                    container: this.geofence.root,
                    after: null,
                    containerKey: -1
                }];
            }

            // Build the exclusions lists:
            let excludedContainersRange = [-1, -1];
            let excludedContainerAndAfters = [];

            if (e.sourceKind == "row") {
                let rowIndex = e.sourceValue !== undefined ? parseInt(e.sourceValue) : this._draggingRow.key;
                let row = this.rows[rowIndex];

                if (row !== undefined) {
                    excludedContainersRange = row.keysRange;
                }

                excludedContainerAndAfters.push([row.parentNode, row.node]);
                excludedContainerAndAfters.push([row.parentNode, row.parentNode.getPreviousSibling(row.node)]);
            }

            // Use the drops and exclusions to find the appropriate drop:
            let drop = null;

            for (let candidate of Array.from(drops)) {
                let inExcludedContainers = 
                    excludedContainersRange[0] <= candidate.containerKey && candidate.containerKey < excludedContainersRange[1];

                let inExcludedAfter = false;

                for (let [container, after] of excludedContainerAndAfters) {
                    if (candidate.container == container && candidate.after == after) inExcludedAfter = true;
                }

                if (candidate.indent <= e.columnIndent && !inExcludedContainers && !inExcludedAfter) {
                    drop = candidate;
                }
            }

            return drop;
        },
        createNodeUnderRoot(constructor) {
            let node = new constructor();

            this.geofence.root.add(node);

            this.updateTree(true);
        },
        handleRowSelect(row) {
            console.log(row.node.id);
        },
        _completeDragOver(drop, left, top) {
            if (drop) {
                this.dropperPosition = [left + drop.indent * this.indentWidth, top];
            } else {
                this.dropperPosition = null;
            }
        },
        _completeDragDrop(drop, sourceKind, sourceValue, sourceOptions) {
            if (drop != null) {
                if (sourceKind == "constructor") {
                    let constructorMethod = nodes[sourceValue];

                    let node = new constructorMethod();

                    Object.assign(node.properties, sourceOptions);

                    drop.container.insert(node, drop.after);
                } else if (sourceKind == "row") {
                    let row = this.rows[sourceValue];

                    row.parentNode.delete(row.node);

                    drop.container.insert(row.node, drop.after);
                }
                
                this.updateTree(true);
            }
        },
        handleRowDragOver(e) {
            let top = e.rowOffset == 0 ? e.bounds.top : e.bounds.bottom;

            let drop = this._findDrop(e);

            this._completeDragOver(drop, e.bounds.left, top);
        },
        handleRowDragDrop(e) {
            this.dropperPosition = null;

            let drop = this._findDrop(e);

            this._completeDragDrop(drop, e.sourceKind, e.sourceValue, e.sourceOptions);
        },
        updateTree(nodesChanged) {
            let selectedNode = this.selectedRow != null ? this.selectedRow.node : null;

            this.rows = flattenTree(this.geofence.root);

            if (nodesChanged) window.bus.notifyGeofenceNodeChanged(this.geofence);

            this.selectedRow = null;

            if (selectedNode != null) {
                for (let row of this.rows) {
                    if (row.node == selectedNode) this.selectedRow = row;
                }
            }
        },
        handleDragLeave(e) {
            e.preventDefault();
            e.stopPropagation();

            if (e.target == this.$el) this.dropperPosition = null;
        },
        handleRowDraggingStarted(row) {
            this._draggingRow = row;
        },
        handleContainerDragEnterOrOver(e) {
            e.preventDefault();

            this.dropperPosition = null;
        },
        handleContainerDragDrop(e) {
            e.preventDefault();

            let [sourceKind, sourceValue, sourceOptions] = unpackNodeDragSource(e);
            
            this._completeDragDrop({
                indent: 0,
                container: this.geofence.root,
                after: this.geofence.root.getLastChild(),
                containerKey: -1
            }, sourceKind, sourceValue, sourceOptions);
        },
        handleDelete() {
            if (this.selectedRow == null) return;

            this.selectedRow.parentNode.delete(this.selectedRow.node);

            this.updateTree(true);
        },
        handleMoveToFragment() {
            this.toFragmentName = "";
            this.$refs.toFragmentModal.open();
        },
        handleCompleteMoveToFragment() {
            this.$refs.toFragmentModal.close();

            if (this.selectedRow == null) return;

            // Replace the node with a fragment:
            let movingNode = this.selectedRow.node;
            let fragmentNode = new nodes.Fragment();

            this.selectedRow.parentNode.replace(movingNode, fragmentNode);

            // Create the fragment:
            let fragmentGeofence = geofences.addGeofence({ name : this.toFragmentName, kind: "fragment" });
            fragmentGeofence.root.add(movingNode);

            // Point the fragment node to the fragment:
            fragmentNode.properties.geofence = fragmentGeofence.id;

            this.updateTree(true);
        },
        handleShowXml() {
            this.showXmlContents = this.geofence.root.serialize();

            this.$refs.showXmlModal.open();
        }
    },
    mounted() {
        this.updateTree(true);
    }
}
</script>

<style scoped lang="less">
.container
{
    overflow-y: scroll;
    padding: 8px;
    box-sizing: border-box;
}
.add-button
{
    display: inline-block;
    font-size: 12px;
    color: gray;
    background-color: #eee;
    padding: 3px 10px;
    border-radius: 4px;
}
.error
{
    text-align: center;
    border: 1px solid red;
    background-color: mix(red, white, 10%);
    padding: 8px;
    font-size: 80%;
    border-radius: 4px;
}
.buttons > *
{
    margin-left: 8px;
}
</style>
